// iter84.61 — one live signal row: status icon, severity chip, waived
// context, and an inline "Waive" flow for failing HIGH signals (critical
// is never waivable — the backend rejects it too).
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { StatusIcon, SeverityChip } from "@/components/site-admin/values-uat/pills";

export function SignalRow({ s, onChanged }) {
    const [waiving, setWaiving] = useState(false);
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);

    const waive = async () => {
        if (!reason.trim()) { toast.error("A reason is required to waive"); return; }
        setBusy(true);
        try {
            await api.releaseOpsSignalWaiverCreate({ signal_id: s.id, reason });
            toast.success(`${s.id} waived`);
            setWaiving(false);
            setReason("");
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Waive failed");
        } finally {
            setBusy(false);
        }
    };

    const revoke = async () => {
        setBusy(true);
        try {
            await api.releaseOpsSignalWaiverRevoke(s.id);
            toast.success(`Waiver on ${s.id} revoked`);
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Revoke failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <li
            data-testid={`values-engine-signal-${s.id}`}
            data-status={s.status}
            className="flex items-start gap-2 text-[11px] py-1 border-b border-[#1a1a1a] last:border-b-0"
        >
            <StatusIcon status={s.status} />
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-white">{s.label}</span>
                    <SeverityChip severity={s.severity} />
                    {s.proposed && (
                        <span className="pill text-[8px] font-bold uppercase border bg-amber-900/30 text-amber-300 border-amber-800" title="value_group is a steward proposal — not yet committed">
                            proposed
                        </span>
                    )}
                    {s.status === "fail" && s.severity === "high" && !waiving && (
                        <button
                            type="button"
                            onClick={() => setWaiving(true)}
                            data-testid={`values-engine-waive-btn-${s.id}`}
                            className="ml-auto text-[9px] font-bold uppercase px-1.5 py-0.5 border border-violet-700 text-violet-300 rounded hover:bg-violet-900/30"
                        >
                            Waive
                        </button>
                    )}
                    {s.status === "waived" && (
                        <button
                            type="button"
                            disabled={busy}
                            onClick={revoke}
                            data-testid={`values-engine-revoke-waiver-btn-${s.id}`}
                            className="ml-auto text-[9px] font-bold uppercase px-1.5 py-0.5 border border-zinc-700 text-zinc-400 rounded hover:text-white"
                        >
                            Revoke waiver
                        </button>
                    )}
                </div>
                <p className="text-[10px] text-zinc-400 mt-0.5 leading-relaxed">{s.detail}</p>
                {s.status === "waived" && s.waiver && (
                    <p className="text-[9px] text-violet-300/80 mt-0.5" data-testid={`values-engine-waiver-info-${s.id}`}>
                        WAIVED — “{s.waiver.reason}” · {s.waiver.actor} · {s.waiver.created_at?.slice(0, 10)}
                    </p>
                )}
                {waiving && (
                    <div className="flex items-center gap-1.5 mt-1">
                        <input
                            type="text"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Reason (required, audited)"
                            data-testid={`values-engine-waive-reason-${s.id}`}
                            className="brutal-input text-[10px] py-0.5 px-1.5 flex-1"
                        />
                        <button type="button" disabled={busy} onClick={waive} data-testid={`values-engine-waive-confirm-${s.id}`} className="brutal-btn text-[9px] py-0.5 px-2 bg-violet-700 text-white">
                            Confirm
                        </button>
                        <button type="button" onClick={() => setWaiving(false)} className="brutal-btn text-[9px] py-0.5 px-2">
                            Cancel
                        </button>
                    </div>
                )}
            </div>
        </li>
    );
}
